import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StripeConnectStatus {
  /** Platform-level Stripe is set up (secret key present etc.). */
  configured: boolean;
  connected: boolean;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
}

export const EMPTY_STRIPE_STATUS: StripeConnectStatus = {
  configured: false,
  connected: false,
  accountId: null,
  chargesEnabled: false,
  payoutsEnabled: false,
  detailsSubmitted: false,
};

type StripeConnectAction = 'status' | 'onboard' | 'disconnect';

async function invokeStripeConnect<T>(action: StripeConnectAction, organizationId: string): Promise<T> {
  const { data, error } = await supabase.functions.invoke('stripe-connect', {
    body: { action, organizationId },
  });
  if (error) {
    // supabase-js hides the JSON body of non-2xx responses inside error.context.
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === 'function') {
      try {
        const body = await context.json();
        if (body?.error) throw new Error(String(body.error));
      } catch (inner) {
        if (inner instanceof Error && inner.message) throw inner;
      }
    }
    throw error;
  }
  if (data && typeof data === 'object' && 'error' in data && (data as { error?: unknown }).error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

export const stripeConnectKey = (orgId: string | null | undefined) => ['stripe-connect-status', orgId] as const;

export function useStripeConnectStatus(orgId: string | null | undefined) {
  return useQuery({
    queryKey: stripeConnectKey(orgId),
    enabled: Boolean(orgId),
    retry: false,
    queryFn: async (): Promise<StripeConnectStatus> => {
      const data = await invokeStripeConnect<Partial<StripeConnectStatus> | null>('status', orgId!);
      return { ...EMPTY_STRIPE_STATUS, ...(data ?? {}) };
    },
  });
}

/** Returns the Stripe-hosted onboarding URL; the caller redirects the browser. */
export function useStripeOnboard(orgId: string | null | undefined) {
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const data = await invokeStripeConnect<{ url?: string }>('onboard', orgId!);
      if (!data?.url) throw new Error('Stripe did not return an onboarding link.');
      return data.url;
    },
  });
}

export function useStripeDisconnect(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => invokeStripeConnect<{ ok: boolean }>('disconnect', orgId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stripeConnectKey(orgId) });
      // Server also turns checkout_enabled off.
      queryClient.invalidateQueries({ queryKey: ['payments-admin', orgId] });
    },
  });
}

/** Cards can be taken online only when the connected account is allowed to charge. */
export const canTakeCardPayments = (status: StripeConnectStatus | undefined) =>
  Boolean(status?.connected && status.chargesEnabled);

export const maskStripeAccountId = (id: string | null | undefined) => (id ? `acct_…${id.slice(-4)}` : '');
